import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Grid3x3, Sparkles, Video, Boxes, Mail, Gauge
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Button } from "../components/ui/button";
import { PARTNERS, CLIENTS } from "../mock";
import { LogoImage } from "../components/LogoImage";

/* Two lightweight, on-brand abstract illustrations (used at MOST twice — hero + one feature) */
const IMG = {
  hero:    "https://images.unsplash.com/photo-1596496050827-8299e0220de1?w=1400&q=80&auto=format&fit=crop",
  aiTutor: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1400&q=80&auto=format&fit=crop",
};

const STATS = [
  { value: "4000+", label: "Professionals Upskilled" },
  { value: "25+",   label: "Global Technology Partners" },
  { value: "500+",  label: "Enterprise Batches Delivered" },
];

const WHY_MATTERS = [
  "Generic course libraries are broad but shallow, and rarely mapped to real proficiency levels.",
  "Learners stall the moment they hit a hard concept, with no help in reach.",
  "L&D can't easily tell beginner content from advanced.",
];

const WHAT_CARDS = [
  {
    icon: Gauge,
    text: "Three proficiency tiers — Beginner (2–4 hrs), Intermediate (6–8 hrs), Advanced (8–12 hrs).",
  },
  {
    icon: Grid3x3,
    text: "21 technology domains — full-stack coverage across AI, cloud, security, data, DevOps, SAP, and more.",
  },
  {
    icon: Sparkles,
    text: "In-module AI Learning Assistant — ask questions and get concept explanations and code walkthroughs without leaving the lesson.",
  },
  {
    icon: Video,
    text: "Rich lessons — video, inline example code, and key-concept summaries.",
  },
  {
    icon: Boxes,
    text: "LMS-native — courses map directly into Enterprise LMS learning paths.",
  },
];

const DOMAINS = [
  "AI & Machine Learning", "BFSI", "CheckPoint", "Cloud Computing",
  "Compliance & Governance", "Cybersecurity", "Database & Big Data",
  "Data Science & Analytics", "DevOps & Cloud Native", "ERP & Enterprise Applications",
  "IT Service Management", "IoT & Embedded Systems", "Microsoft Technologies",
  "Networking & Infrastructure", "Project Management & Agile", "QA & Testing",
  "Salesforce & CRM", "SAP", "Software Development & Programming",
  "UI/UX Design", "Other Technology",
];

const WHO = [
  "Enterprises standardising self-paced upskilling.",
  "Teams blending self-paced study with instructor-led programs.",
  "Professionals pursuing certifications on their own schedule.",
];

const ScreenshotFrame = ({ src, alt }) => (
  <div className="relative rounded-2xl overflow-hidden border border-gray-200 bg-white shadow-[0_20px_50px_-12px_rgba(21,52,140,0.25)]">
    <img src={src} alt={alt} className="w-full h-auto block" loading="lazy" />
  </div>
);

const ELearningPage = () => {
  return (
    <div className="bg-white min-h-screen">
      <Header />

      {/* 1) HERO */}
      <PageHero
        eyebrow="E-LEARNING"
        title="Empowering Enterprise Learning at Scale"
        subtitle="500+ Enterprise Batches | 100,000+ Learning Hours"
      />

      <section className="max-w-7xl mx-auto px-4 pt-4 pb-6">
        <Reveal>
          <p className="max-w-3xl mx-auto text-center text-gray-600 leading-relaxed text-lg" data-testid="elearning-hero-body">
            Upskill with expert-designed, industry-aligned courses covering AI, Cloud, Cybersecurity, Data, DevOps, SAP, Software Development, and more. Every course includes structured learning paths, hands-on content, and an AI Learning Assistant to accelerate skill development.
          </p>
        </Reveal>
        <Reveal delay={100}>
          <div className="mt-8">
            <ScreenshotFrame src={IMG.hero} alt="Empowering Enterprise Learning at Scale" />
          </div>
        </Reveal>
        <Reveal delay={150}>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/contact">
              <Button className="bg-[#15348C] hover:bg-[#0A1F5C] text-white font-semibold px-6 h-12 rounded-md shadow-lg shadow-[#15348C]/25">
                Talk to Our Expert <ArrowRight size={17} className="ml-1" />
              </Button>
            </Link>
            <Link to="/demo">
              <Button className="bg-white text-[#15348C] border-2 border-[#15348C] hover:bg-[#15348C] hover:text-white font-semibold px-6 h-12 rounded-md">
                Book a Demo
              </Button>
            </Link>
          </div>
        </Reveal>
      </section>

      {/* 2) STAT BAND */}
      <section className="py-14 bg-[#F8F9FA] border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-3 gap-6">
          {STATS.map((s, i) => (
            <Reveal key={s.label} variant="up" delay={i * 100}>
              <div className="text-center bg-white border border-gray-100 rounded-2xl py-8 px-4 hover:shadow-lg transition-all">
                <div className="text-5xl lg:text-6xl font-bold text-[#15348C]">
                  {s.value}
                </div>
                <div className="mt-2 text-sm font-semibold tracking-wide text-gray-600">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 3) WHY IT MATTERS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-3xl">
              <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">WHY IT MATTERS</div>
              <h2 className="text-[#15348C] leading-tight">The gaps generic course libraries leave behind.</h2>
            </div>
          </Reveal>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {WHY_MATTERS.map((w, i) => (
              <Reveal key={w} variant="up" delay={i * 90}>
                <div className="bg-white border border-gray-100 rounded-2xl p-6 h-full hover:shadow-lg transition-all">
                  <div className="w-10 h-10 rounded-lg bg-[#8BD835]/15 flex items-center justify-center mb-4">
                    <span className="text-[#4F9622] font-bold">{i + 1}</span>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{w}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 4) WHAT IT IS */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-3xl">
              <div className="text-xs font-bold tracking-widest text-[#6BBE32] mb-3">WHAT IT IS</div>
              <h2 className="text-[#15348C] leading-tight">A curated self-paced course library — mapped to real proficiency.</h2>
              <p className="mt-4 text-gray-600 leading-relaxed">
                2,500+ courses across 21 domains — with every course mapped to a clear proficiency tier, so learners follow a real progression path.
              </p>
            </div>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHAT_CARDS.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.text} variant="up" delay={i * 80}>
                  <div className="bg-white border border-gray-100 rounded-2xl p-6 h-full hover:border-[#6BBE32]/40 hover:shadow-xl transition-all group">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#6BBE32] to-[#4F9622] flex items-center justify-center shadow-lg shadow-[#6BBE32]/25 mb-4">
                      <Icon size={22} className="text-white" />
                    </div>
                    <p className="text-gray-700 leading-relaxed">{c.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5) DOMAINS GRID */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-3xl mb-8">
              <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">DOMAINS</div>
              <h2 className="text-[#15348C] leading-tight">21 technology domains covered end-to-end.</h2>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <div className="flex flex-wrap gap-2.5">
              {DOMAINS.map((d) => (
                <span
                  key={d}
                  className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-[#15348C]/5 text-[#15348C] border border-[#15348C]/15 hover:bg-[#15348C] hover:text-white hover:border-[#15348C] transition-colors cursor-default"
                >
                  {d}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6) FEATURE HIGHLIGHT — AI Learning Assistant */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-3xl mb-12">
              <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">FEATURE HIGHLIGHT</div>
              <h2 className="text-[#15348C] leading-tight">AI Learning Assistant: Your Personal Tutor in Every Lesson</h2>
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <Reveal variant="left">
              <ScreenshotFrame src={IMG.aiTutor} alt="AI Learning Assistant: Your Personal Tutor in Every Lesson" />
            </Reveal>
            <Reveal variant="right" delay={80}>
              <p className="text-gray-600 leading-relaxed text-lg">
                Learning becomes easier with an intelligent assistant that provides instant, context-aware guidance exactly when you need it. Whether you&apos;re exploring a new concept, debugging code, or preparing for certification, the AI Learning Assistant helps you stay focused and make faster progress.
              </p>

              <h4 className="mt-8 text-[#15348C]" style={{ fontSize: "1.25rem" }}>Key Benefits</h4>
              <ul className="mt-4 space-y-3" data-testid="feature-highlight-benefits">
                {[
                  "Instant explanations for complex concepts",
                  "Step-by-step coding guidance and debugging support",
                  "Context-aware answers without leaving the course",
                  "Personalized learning assistance based on your progress",
                  "Faster skill development with uninterrupted learning",
                ].map((b) => (
                  <li key={b} className="flex gap-3 text-gray-700">
                    <CheckCircle2 size={20} className="text-[#6BBE32] shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{b}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 7) PROOF STRIP */}
      <section className="py-16 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center text-xs font-bold tracking-widest text-gray-500 mb-6">LEARNING TRUSTED BY GLOBAL ORGANIZATIONS</div>
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-5 opacity-80">
              {PARTNERS.slice(0, 10).map((p) => (
                <LogoImage key={p.name} src={p.logo} alt={p.name}
                  className="h-8 max-w-[120px] object-contain"
                  fallbackClassName="text-[#15348C] font-bold text-sm" />
              ))}
            </div>
            {CLIENTS && CLIENTS.length > 0 && (
              <div className="mt-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 opacity-70">
                {CLIENTS.slice(0, 8).map((c) => (
                  <LogoImage key={c.name} src={c.logo} alt={c.name}
                    className="h-7 max-w-[110px] object-contain"
                    fallbackClassName="text-gray-500 font-semibold text-sm" />
                ))}
              </div>
            )}
          </Reveal>
        </div>
      </section>

      {/* 8) WHO IT'S FOR */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-3xl mb-10">
              <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">WHO IT&apos;S FOR</div>
              <h2 className="text-[#15348C] leading-tight">Made for teams that live by self-paced learning.</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {WHO.map((w, i) => (
              <Reveal key={w} variant="up" delay={i * 90}>
                <div className="flex gap-4 bg-white border border-gray-100 rounded-2xl p-6 h-full hover:shadow-lg transition-all">
                  <CheckCircle2 size={22} className="text-[#6BBE32] shrink-0 mt-0.5" />
                  <p className="text-gray-700 leading-relaxed">{w}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 9) CTA BAND */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#15348C] to-[#0A1F5C] px-8 py-12 lg:px-14 lg:py-16 text-white">
              <div className="absolute -top-24 -right-24 w-80 h-80 bg-[#6BBE32]/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-[#8BD835]/20 rounded-full blur-3xl" />
              <div className="relative grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-white leading-tight">Give your team a library that actually maps to skill.</h2>
                </div>
                <div className="flex flex-wrap gap-4 lg:justify-end items-center">
                  <Link to="/contact">
                    <Button className="bg-[#8BD835] hover:bg-[#6BBE32] text-white font-semibold px-7 h-12 rounded-md shadow-lg shadow-[#8BD835]/30">
                      Talk to Our Expert <ArrowRight size={17} className="ml-1" />
                    </Button>
                  </Link>
                  <a href="mailto:info@skillsgain.com" data-testid="elearning-cta-email"
                     className="inline-flex items-center gap-2 text-white/90 hover:text-[#8BD835] font-semibold transition-colors">
                    <Mail size={18} /> info@skillsgain.com
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ELearningPage;
